/**
 * 収録動画からシナリオを作る (docs/features/video-import-scenario-key-points-plan.md)。
 *
 * 動画エディタ側: 「この動画からシナリオを作る」で要点の整理 (digest・サーバーの非同期ジョブ) を作らせて待ち、
 * 整理済みの要点と話題をシナリオ生成画面へ渡す (§4.3 / §5.1)。要点の絞り込みはシナリオ生成画面で行う (§5.2)。
 * 材料 (「収録動画の内容」) の組み立てはサーバーが受付時に行う (親計画 §4.4)。
 */
import { apiClient } from '../../services/apiClient';
import type { LinkedProduct } from './api';
import type { EditProject } from './schema/project';

export interface DigestPoint {
  id: string;
  text: string;
  rank: number;
  sourceSegmentIds: string[];
  sourceKeyPoints: string[];
}

export interface Digest {
  id: string;
  version: number;
  inputHash: string;
  points: DigestPoint[];
  input?: { chars?: number; bodyLimit?: number | null; truncatedSegmentIds?: string[]; topicCount?: number } | null;
  model?: string;
  builtAt?: string;
}

export interface DigestTopic {
  id: string;
  title: string;
  kind: 'talk' | 'demo';
  startSec: number | null;
  keyPoints: string[];
}

export type DigestStatus = 'ready' | 'in_progress' | 'failed' | 'expired' | 'outdated' | 'none';

export interface DigestStatusResponse {
  status: DigestStatus;
  digest: Digest | null;
  digestPending: { runId: string; inputHash: string; startedAt: string; failedAt?: string; error?: string } | null;
  inputHash: string;
  revision: number;
  label: string;
  topics: DigestTopic[];
}

/** シナリオ生成画面へ渡す選択 (遷移の state)。要点の選択は画面側で持つ */
export interface SourceVideoSelection {
  projectId: string;
  label: string;
  digest: Digest;
  topics: DigestTopic[];
  totalSegments: number;
  /** 編集に紐づけた手動登録の案件 (products.id)。asin と両方 null ならカスタム入力モードで案件名を使う */
  productId: string | null;
  /** 編集に紐づけた Amazon 案件の ASIN (シナリオ画面は ASIN モードで開く) */
  asin: string | null;
  productName: string;
}

/** 想定尺に応じた既定のチェック件数 (BE の suggested_point_ids と同じ規則。§5.2) */
export function suggestedPointIds(digest: Pick<Digest, 'points'>, durationSec: number): string[] {
  const n = durationSec <= 30 ? 3 : durationSec <= 45 ? 4 : 5;
  return [...digest.points].sort((a, b) => a.rank - b.rank).slice(0, n).map((p) => p.id);
}

function asRecord(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
}

/** 画面の文字 (semantic.textOnScreen) の先頭を案件名の初期値にする。無ければ編集名。 */
export function defaultSourceVideoProductName(project: Pick<EditProject, 'sourceClips'> | null | undefined, fallback: string): string {
  for (const clip of project?.sourceClips ?? []) {
    const analysis = asRecord((clip as { analysis?: unknown }).analysis);
    const semantic = asRecord(analysis?.semantic);
    const texts = Array.isArray(semantic?.textOnScreen) ? semantic!.textOnScreen : [];
    const first = texts.find((t): t is string => typeof t === 'string' && t.trim() !== '');
    if (first) return first.trim();
  }
  return fallback;
}

export function formatTopicTime(sec: number | null): string {
  if (sec == null || !Number.isFinite(sec)) return '--:--';
  const total = Math.floor(sec);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

// ------------------------------------------------------------
// API
// ------------------------------------------------------------

export interface SourceVideoBriefPreview {
  ok: boolean;
  chars?: number;
  omitted?: string[];
  point_count?: number;
  segment_count?: number;
  total_segments?: number;
  error?: string;
  error_code?: string;
  max_points?: number;
}

/** 「収録動画の内容」の事前確認 (保存しない)。要点の不一致 (stale / outdated) は 409 で reject。 */
export async function previewSourceVideoBrief(
  projectId: string,
  body: { digest_id: string; point_ids: string[]; duration_sec: number },
): Promise<SourceVideoBriefPreview> {
  const { data } = await apiClient.post<SourceVideoBriefPreview>(`/video-edit-projects/${projectId}/source-video-brief`, body);
  return data;
}

/** 編集に案件 (手動登録 / Amazon) を紐づける / 外す (素材起点のみ)。応答は解決済みの案件 (外したときは null)。 */
export async function setEditProjectProduct(
  projectId: string,
  link: { product_type: 'manual' | 'amazon'; product_id: string } | null,
): Promise<{ id: string; linked_product: LinkedProduct | null }> {
  const { data } = await apiClient.patch<{ id: string; linked_product: LinkedProduct | null }>(
    `/video-edit-projects/${projectId}/product`,
    link ? { product_type: link.product_type, product_id: link.product_id } : { product_id: null },
  );
  return data;
}

export async function getDigest(projectId: string): Promise<DigestStatusResponse> {
  const { data } = await apiClient.get<DigestStatusResponse>(`/video-edit-projects/${projectId}/digest`);
  return data;
}

export async function createDigest(projectId: string): Promise<{ status: 'ready' | 'in_progress'; digest?: Digest; job_id?: string; revision: number }> {
  const { data } = await apiClient.post<{ status: 'ready' | 'in_progress'; digest?: Digest; job_id?: string; revision: number }>(`/video-edit-projects/${projectId}/digest`, {});
  return data;
}

export const DIGEST_POLL_INTERVAL_MS = 3000;
export const DIGEST_WAIT_MAX_MS = 185_000;

export class DigestWaitError extends Error {
  readonly status: DigestStatus | 'timeout' | 'cancelled';

  constructor(message: string, status: DigestStatus | 'timeout' | 'cancelled') {
    super(message);
    this.name = 'DigestWaitError';
    this.status = status;
  }
}

/**
 * 要点の整理を作らせて ready になるまで待つ (§4.3)。
 * - POST → 3 秒ごとに GET。in_progress は最長 185 秒まで待つ
 * - failed (stale_input) は 1 回だけ自動で作り直す。それ以外の failed / expired は利用者の操作を待つ (reject)
 * - outdated / none を見たら 1 回だけ POST し直す (作成直後の競合)
 */
export async function ensureDigest(
  projectId: string,
  options?: {
    onStatus?: (status: DigestStatus) => void;
    sleep?: (ms: number) => Promise<void>;
    now?: () => number;
    /** 画面側の待ちを無効にする (別の編集へ移った・アンマウント)。true になったら待つのをやめて reject (サーバーのジョブは続く) */
    isCancelled?: () => boolean;
  },
): Promise<DigestStatusResponse> {
  const sleep = options?.sleep ?? ((ms: number) => new Promise<void>((r) => window.setTimeout(r, ms)));
  const now = options?.now ?? (() => Date.now());
  const cancelled = () => options?.isCancelled?.() === true;
  let retriedStale = false;
  let retriedMissing = false;
  const first = await createDigest(projectId);
  if (cancelled()) throw new DigestWaitError('要点の整理の待機を中止しました', 'cancelled');
  options?.onStatus?.(first.status);
  if (first.status === 'ready') return getDigest(projectId);
  const deadline = now() + DIGEST_WAIT_MAX_MS;
  for (;;) {
    await sleep(DIGEST_POLL_INTERVAL_MS);
    if (cancelled()) throw new DigestWaitError('要点の整理の待機を中止しました', 'cancelled');
    const s = await getDigest(projectId);
    if (cancelled()) throw new DigestWaitError('要点の整理の待機を中止しました', 'cancelled');
    options?.onStatus?.(s.status);
    if (s.status === 'ready') return s;
    if (s.status === 'in_progress') {
      if (now() > deadline) throw new DigestWaitError('要点の整理に時間がかかりすぎました。もう一度お試しください', 'timeout');
      continue;
    }
    if (s.status === 'failed') {
      if (s.digestPending?.error === 'stale_input' && !retriedStale) {
        retriedStale = true;
        await createDigest(projectId);
        continue;
      }
      throw new DigestWaitError('要点を整理できませんでした。もう一度お試しください', 'failed');
    }
    if (s.status === 'expired') throw new DigestWaitError('要点の整理が時間切れになりました。もう一度お試しください', 'expired');
    // outdated / none: 作成直後に原稿が変わった等。1 回だけ作り直す
    if (!retriedMissing) {
      retriedMissing = true;
      await createDigest(projectId);
      continue;
    }
    throw new DigestWaitError('要点を整理できませんでした。もう一度お試しください', s.status);
  }
}

/** 省略 (omitted) の利用者向け文言。 */
export function describeOmitted(omitted: ReadonlyArray<string> | undefined): string[] {
  const out: string[] = [];
  for (const o of omitted ?? []) {
    if (o === 'summary') out.push('動画全体の要約は省きました');
    else if (o === 'screen_text') out.push('動画全体の画面の文字は省きました');
    else if (o === 'segment_screen_text') out.push('話題ごとの画面の文字は省きました');
    else if (o === 'body:0') out.push('根拠の話題の本文は省き、見出しだけにしました');
    else if (o.startsWith('body:')) out.push(`根拠の話題の本文を短くしました (${o.slice('body:'.length)}字)`);
    else if (o.startsWith('key_points:')) out.push(`話題ごとの要点を${o.slice('key_points:'.length)}件に減らしました`);
  }
  return Array.from(new Set(out));
}
